import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@roger/ui-components';

import { getChatDirectory, type DirectoryRow } from '../../services/api';
import { useStore } from '../../store';
import type { EntryRef } from './memberEntries';
import { usePickableRoles } from './useGroupAdminQueries';

const DEBOUNCE_MS = 200;
const DIRECTORY_LIMIT = 50;

type Person = { id: string; label: string };

type MemberPickerProps = {
  selected: EntryRef[];
  onPick: (entry: EntryRef, label: string) => void;
  /** Blocks picking a result, not the input: while a save or its refetch is
   *  in flight a pick would build its body from stale entries, but the admin
   *  can keep typing the next name. */
  pickDisabled?: boolean;
};

export function MemberPicker({
  selected,
  onPick,
  pickDisabled,
}: MemberPickerProps) {
  const user = useStore(state => state.user);
  const { data: roles = [] } = usePickableRoles();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const searchText = debouncedQuery.trim();
  const directory = useQuery({
    queryKey: ['groups', 'picker-directory', searchText],
    enabled: searchText !== '',
    queryFn: async () => {
      const { data } = await getChatDirectory({
        query: { q: searchText, limit: DIRECTORY_LIMIT },
        throwOnError: true,
      });
      return (data ?? []) as DirectoryRow[];
    },
  });

  const typed = query.trim().toLowerCase();
  const isSelected = (kind: EntryRef['kind'], id: string) =>
    selected.some(entry => entry.kind === kind && entry.id === id);

  const visibleRoles = roles.filter(
    role =>
      !isSelected('role', role.id) && role.name.toLowerCase().includes(typed)
  );

  const people: Person[] = [];
  if (typed !== '') {
    for (const row of directory.data ?? []) {
      if (row.type === 'user') people.push({ id: row.id, label: row.label });
    }
    // The directory excludes the caller, and an administrator must be able to
    // add themselves.
    if (
      user &&
      user.name.toLowerCase().includes(typed) &&
      !people.some(person => person.id === user.id)
    ) {
      people.push({ id: user.id, label: user.name });
    }
  }
  const visiblePeople = people.filter(person => !isSelected('user', person.id));

  const settled =
    query.trim() === searchText && !directory.isFetching && directory.isFetched;

  const pick = (entry: EntryRef, label: string) => {
    if (pickDisabled) return;
    onPick(entry, label);
    setQuery('');
  };

  return (
    <Command shouldFilter={false} className='h-auto overflow-visible'>
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverAnchor asChild>
          <div ref={anchorRef} onPointerDown={() => setOpen(true)}>
            <CommandInput
              placeholder='Add people or roles — type a name'
              value={query}
              onValueChange={value => {
                setQuery(value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
            />
          </div>
        </PopoverAnchor>
        {/* Above the input, never flipped below: the picker sits over the
            dialog and drawer buttons, and a list below would cover them. */}
        <PopoverContent
          align='start'
          side='top'
          avoidCollisions={false}
          className='w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)] p-0'
          onOpenAutoFocus={event => event.preventDefault()}
          onInteractOutside={event => {
            if (anchorRef.current?.contains(event.target as Node)) {
              event.preventDefault();
            }
          }}
        >
          <CommandList className='max-h-[min(18rem,var(--radix-popover-content-available-height))] overscroll-contain'>
            {visibleRoles.length > 0 && (
              <CommandGroup heading='Roles'>
                {visibleRoles.map(role => (
                  <CommandItem
                    key={`role:${role.id}`}
                    value={`role:${role.id}`}
                    disabled={pickDisabled}
                    onSelect={() =>
                      pick({ kind: 'role', id: role.id }, role.name)
                    }
                  >
                    <span className='truncate'>{role.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {typed === '' ? (
              <p className='px-3 py-2 text-sm text-muted-foreground'>
                Type a name to search your colleagues
              </p>
            ) : visiblePeople.length > 0 ? (
              <CommandGroup heading='People'>
                {visiblePeople.map(person => (
                  <CommandItem
                    key={`user:${person.id}`}
                    value={`user:${person.id}`}
                    disabled={pickDisabled}
                    onSelect={() =>
                      pick({ kind: 'user', id: person.id }, person.label)
                    }
                  >
                    <span className='truncate'>{person.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : settled ? (
              <p className='px-3 py-2 text-sm text-muted-foreground'>
                {`No one matches "${query.trim()}"`}
              </p>
            ) : null}
          </CommandList>
        </PopoverContent>
      </Popover>
    </Command>
  );
}
